import { addDoc, collection, doc, getCountFromServer, getDocs, limit, query, QueryDocumentSnapshot, SnapshotOptions, Timestamp, where, WithFieldValue, writeBatch, runTransaction, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { firebaseDataConverter } from "./Converters/FirebaseConverter";
import { ProductKey, ProductKeyFromDbMapper, ProductKeyToDbMapper } from "../models/ProductKey";
import { ProductKeyDb } from "./models/ProductKeyDb";
import { v4 as uuidv4 } from 'uuid';

class ProductKeyService {
    private static instance: ProductKeyService | null = null;

    private static tableName: string = "productKey";
    public static getInstance(): ProductKeyService {
        if (this.instance === null) {
            this.instance = new ProductKeyService();
        }
        return this.instance;
    }

    public async getProductKeysAsync(): Promise<ProductKey[]> {
        const documents = await getDocs(query(collection(db, ProductKeyService.tableName)).withConverter<ProductKeyDb>(firebaseDataConverter))
        const mapped = documents.docs.map(doc => {
            var data = doc.data()
            return ProductKeyFromDbMapper(data);
        });
        return mapped;
    }

    public async getProductKeysByCompanyAndProductAsync(companyGuid: string, productGuid: string): Promise<ProductKey[]> {

        const q = query(collection(db, ProductKeyService.tableName), where("companyGuid", "==", companyGuid), where("productGuid", "==", productGuid)).withConverter<ProductKeyDb>(firebaseDataConverter);
        const documents = await getDocs(q)
        const mapped = documents.docs.map(doc => {
            var data = doc.data()
            return ProductKeyFromDbMapper(data);
        });
        return mapped;
    }

    public async getUnassignedProductKeysAsync(companyGuid: string, productGuid: string): Promise<ProductKey[]> {
        if (companyGuid == undefined) return [];
        const q = query(collection(db, ProductKeyService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid),
            where("voucherGuid", "==", null)).withConverter<ProductKeyDb>(firebaseDataConverter);
        const documents = await getDocs(q)
        const mapped = documents.docs.map(doc => {
            var data = doc.data()
            return ProductKeyFromDbMapper(data);
        });
        return mapped;
    }

    public async getProductKeyCountByCompanyAndProductAsync(companyGuid: string, productGuid: string): Promise<number> {
        const q = query(collection(db, ProductKeyService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid)).withConverter<ProductKeyDb>(firebaseDataConverter);
        const count = await getCountFromServer(q)
        return count.data().count;
    }

    public async getUnassignedProductKeyCountAsync(companyGuid: string, productGuid: string): Promise<number> {
        const q = query(collection(db, ProductKeyService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid),
            where("voucherGuid", "==", null)).withConverter<ProductKeyDb>(firebaseDataConverter);
        const count = await getCountFromServer(q)
        return count.data().count;
    }

    /* Mirrors the query validateAndAssignVoucher runs when a voucher is redeemed.
       A key missing its deletedDate field is invisible to that query, so this can
       be lower than the unassigned count above. */
    public async getRedeemableProductKeyCountAsync(companyGuid: string, productGuid: string): Promise<number> {
        const q = query(collection(db, ProductKeyService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid),
            where("voucherGuid", "==", null),
            where("deletedDate", "==", null)).withConverter<ProductKeyDb>(firebaseDataConverter);
        const count = await getCountFromServer(q)
        return count.data().count;
    }

    public async getProductKeyByVoucherGuidAsync(voucherGuid: string): Promise<ProductKey | null> {
        const q = query(collection(db, ProductKeyService.tableName),
            where("voucherGuid", "==", voucherGuid),
            limit(1)).withConverter<ProductKeyDb>(firebaseDataConverter);
        const documents = await getDocs(q)
        if (documents.docs.length < 1) return null;
        return ProductKeyFromDbMapper(documents.docs[0].data());
    }

    /* Moves unassigned keys from one company/product to another so a product that has
       run dry can be served from another product's spare stock.

       Only keys that redemption could actually hand out are moved, and each one is
       re-read inside a transaction immediately before it is written so a key claimed
       by a redemption while this runs is left alone rather than moved out from under
       the customer who just got it. Returns the keys actually moved, which can be
       fewer than requested if that happens. */
    public async transferRedeemableProductKeysAsync(
        sourceCompanyGuid: string,
        sourceProductGuid: string,
        destinationCompanyGuid: string,
        destinationProductGuid: string,
        numberOfKeys: number): Promise<string[]> {

        if (numberOfKeys < 1) return [];

        if (sourceCompanyGuid === destinationCompanyGuid && sourceProductGuid === destinationProductGuid) {
            throw new Error("The source and destination product are the same.");
        }

        const q = query(collection(db, ProductKeyService.tableName),
            where("companyGuid", "==", sourceCompanyGuid),
            where("productGuid", "==", sourceProductGuid),
            where("voucherGuid", "==", null),
            where("deletedDate", "==", null),
            limit(numberOfKeys));

        /* Deliberately unconverted: the document references are needed for the writes. */
        const documents = await getDocs(q);

        let transferredKeys: string[] = [];

        /* Firestore caps a transaction at 500 writes, and every key costs a read as
           well, so work through the selection in chunks. */
        const chunkSize = 100;
        for (let i = 0; i < documents.docs.length; i += chunkSize) {
            const chunk = documents.docs.slice(i, i + chunkSize);

            /* Built fresh inside the callback so a retried transaction does not
               record the same key twice. */
            const chunkKeys = await runTransaction(db, async (transaction) => {
                /* Every read has to happen before any write in a transaction, but they
                   can go out together rather than one round trip at a time. */
                const currentDocuments = await Promise.all(chunk.map((document) => transaction.get(document.ref)));

                let keysMovedInChunk: string[] = [];
                for (const currentDocument of currentDocuments) {
                    const data = currentDocument.data();

                    /* Claimed or removed since the selection was read. */
                    if (data == null) continue;
                    if (data.voucherGuid != null) continue;
                    if (data.deletedDate != null) continue;

                    const now = Timestamp.now();

                    transaction.update(currentDocument.ref, {
                        companyGuid: destinationCompanyGuid,
                        productGuid: destinationProductGuid,
                        /* Keep the first origin recorded so a key moved twice still
                           points at the stock it was originally bought for. */
                        originCompanyGuid: data.originCompanyGuid ?? sourceCompanyGuid,
                        originProductGuid: data.originProductGuid ?? sourceProductGuid,
                        transferredDate: now,
                        lastModifiedDate: now,
                    });
                    keysMovedInChunk.push(data.key);
                }
                return keysMovedInChunk;
            });

            transferredKeys = transferredKeys.concat(chunkKeys);
        }

        return transferredKeys;
    }

    public async addProductKeyAsync(company: ProductKey): Promise<void> {
        var mappedDb = ProductKeyToDbMapper(company);
        await setDoc(doc(db, ProductKeyService.tableName, uuidv4()).withConverter<ProductKeyDb>(firebaseDataConverter), mappedDb);
    }


    public async addProductKeyBatchAsync(productKeys: ProductKey[]): Promise<void> {
        await runTransaction(db, async (transaction) => {
            productKeys.forEach((x) => {
                var mappedDb = ProductKeyToDbMapper(x);
                let documentReference = doc(db, ProductKeyService.tableName, x.guid).withConverter(firebaseDataConverter);

                transaction.set(documentReference, mappedDb);
            });
        });
    }
}

const useProductKeyService = () => {
    return ProductKeyService.getInstance();
}

export default useProductKeyService;

